import { DaySlots } from '@fingertip/creator-proto/gen/fingertip/creator/event_type/v1/event_type_pb'
import { SiteAvailabilityByDayTimeSlot } from '@fingertip/creator-proto/gen/fingertip/creator/nylas/v1/nylas_pb'
import { format } from 'date-fns'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext } from 'react'

import {
  AvailableTimesSkeleton,
  BookerAvailableTimes,
} from '@/components/booking/booker/components/booker-available-times'
import { useSlotsForAvailableDates } from '@/components/booking/utils'
import { TimeZonePicker } from '@/components/shared/time-zone-picker'
import { BookingSelection } from '@/lib/hooks/use-booking-selection'
import { BookingStage } from '@/lib/stores/booker-store'
import { RootStoreContext } from '@/lib/stores/root-store'

type Props = {
  bookingSelection: BookingSelection
  includedDates?: string[]
  slotsByDay?:
    | {
        [key: string]: DaySlots
      }
    | undefined
  availabilitySlots?: SiteAvailabilityByDayTimeSlot[]
  isLoading?: boolean
  nextStage: BookingStage | null
}

/**
 * Renders available time slots for a given date.
 * It will extract the date from the booker store.
 * Next to that you can also pass in the `extraDays` prop, this
 * will also fetch the next `extraDays` days and show multiple days
 * in columns next to each other.
 */
export const BookerStageAvailableSlots = observer(
  ({
    bookingSelection,
    slotsByDay,
    availabilitySlots,
    isLoading,
    nextStage,
  }: Props) => {
    const {
      bookerStore: {
        selectedDate,
        setSelectedTimeslot,
        setSelectedUsers,
        setStage,
        setSelectedGroupClassId,
        setGroupWaitlisted,
      },
    } = useContext(RootStoreContext)

    const date = selectedDate || new Date()

    const onTimeSlotSelect = useCallback(
      (slot: SiteAvailabilityByDayTimeSlot) => {
        setSelectedTimeslot(slot.time?.toDate() ?? null)

        if (
          slot.groupClass &&
          (slot.groupClass.available || 0) <= 0 &&
          slot.groupClass.waitlistEnabled
        ) {
          // Will waitlist the user
          setGroupWaitlisted(true)
        } else {
          setGroupWaitlisted(false)
        }

        if (slot.groupClass && slot.groupClass.id) {
          setSelectedGroupClassId(slot.groupClass.id)
        }
        setSelectedUsers(slot.users)
        setStage(nextStage)
      },
      [
        nextStage,
        setGroupWaitlisted,
        setSelectedGroupClassId,
        setSelectedTimeslot,
        setSelectedUsers,
        setStage,
      ],
    )

    const dates = [date]

    const slotsPerDay = useSlotsForAvailableDates(dates, slotsByDay)

    return (
      <>
        <TimeZonePicker />

        <div className="flex size-full flex-row gap-4">
          {!slotsByDay
            ? // Shows exact amount of days as skeleton.
              Array.from({ length: 1 }).map((_, i) => (
                <AvailableTimesSkeleton key={i} />
              ))
            : slotsPerDay.length > 0 &&
              slotsPerDay.map((slots: { date: Date }) => {
                if (!slots.date) return null

                return (
                  <BookerAvailableTimes
                    className="w-full"
                    key={`${slots.date}`}
                    bookingSelection={bookingSelection}
                    onTimeslotSelect={onTimeSlotSelect}
                    date={slots.date}
                    availabilitySlots={availabilitySlots}
                    availableMonth={
                      selectedDate ? format(slots.date, 'MMMM') : undefined
                    }
                    isLoading={isLoading}
                  />
                )
              })}
        </div>
      </>
    )
  },
)
